/**
 * Phase 49 — Compare analytics event registry.
 *
 * This is the SINGLE entry point for every PostHog event fired from a
 * compare page (or fired as a result of compare-sourced attribution).
 *
 * Scope discipline: this registry is compare-LOCAL. It must not export
 * anything that touches `lib/posthog.ts` or the server PostHog SDK.
 * All events flow through posthog-js client capture, which is already
 * initialized and consent-gated by `components/analytics/posthog-provider.tsx`.
 *
 * Consent: CompareTracker gates compare-page instrumentation and its optional
 * attribution cookie on the authoritative consent cookie plus SDK readiness.
 * This wrapper retains the loaded guard, while the provider's before_send is
 * the final dispatch barrier for every caller (including the cookie-free hub).
 */

import posthog from "posthog-js";
import type { ComparePage } from "@/lib/compare/schema";
import { capturePublishingEvent } from "@/lib/marketing/publishing-analytics";

/* -------------------------------------------------------------------------- */
/*                              Event name registry                           */
/* -------------------------------------------------------------------------- */

/** Every Phase 49 click-style event name. Used by `compareEventAttrs`. */
export type CompareClickEventName =
  | "pricing_cta"
  | "start_free_cta"
  | "related_link"
  | "source_link"
  | "business_plan"
  | "site_control"
  | "handshq_comparison"
  | "contact_sales"
  // #1857 — the first-party Help evidence module. Delegated by
  // <CompareHelpLinkTracker> (which travels with the module) rather than by
  // <CompareTracker>, because two of the four surfaces that render it are ad
  // landings with no CompareTracker mounted.
  | "help_link";

/** Every Phase 49 section-view event name. */
export type CompareViewEventName =
  | "compare_section_viewed"
  | "compare_faq_section_viewed";

/* -------------------------------------------------------------------------- */
/*                         Discriminated event union                          */
/* -------------------------------------------------------------------------- */

/**
 * Discriminated union of every Phase 49 PostHog event.
 *
 * Shared client props (`page_slug`, `page_kind`, `reviewed_at`) are
 * injected by the wrapper from the `page` argument — the arms of the
 * union only carry event-specific fields.
 *
 * `compare_assisted_signup` is the one exception: it fires off the
 * compare page (from the onboarding route post-signup) so it has its
 * own minimal shape with no `page` injection.
 */
export type CompareEvent =
  | {
      event: "compare_pricing_cta_clicked";
      /** Where the anchor was rendered — e.g. "hero" | "internal_links". */
      position?: string;
    }
  | {
      event: "compare_start_free_cta_clicked";
      position?: string;
    }
  | {
      event: "compare_related_link_clicked";
      /** The slug of the related compare page being clicked into. */
      target_slug: string;
    }
  | {
      event: "compare_source_link_clicked";
      target_url: string;
      source_product?: string;
    }
  | {
      event: "compare_business_plan_clicked";
      position?: string;
    }
  | {
      event: "compare_site_control_clicked";
      position?: string;
    }
  | {
      event: "compare_handshq_comparison_clicked";
      position?: string;
    }
  | {
      event: "compare_contact_sales_clicked";
      position?: string;
    }
  | {
      event: "compare_section_viewed";
      /** data-testid of the section that entered the viewport. */
      section_id: string;
    }
  | {
      event: "compare_faq_section_viewed";
      section_id: string;
    }
  | {
      event: "compare_assisted_signup";
      compare_source_slug: string;
      page_kind: "vs" | "roundup" | "alternatives";
    }
  // Phase 50 (HUB-01/HUB-02) — hub pillar events. Fired from the /compare
  // hub, which is NOT a ComparePage, so the wrapper below skips the
  // page_slug / page_kind / reviewed_at injection for these two events.
  | {
      event: "compare_hub_card_clicked";
      /** The slug of the /compare/<slug> clicked into from the hub grid. */
      target_slug: string;
      target_kind: "vs" | "roundup" | "alternatives";
    }
  | {
      event: "compare_hub_filter_applied";
      filter_kind: "vs" | "roundup" | "alternatives" | "all";
      filter_size: "sole-trader" | "2-5" | "6-20" | "20-100" | "100-plus" | "all";
    }
  // #1857 — a click on the first-party Help evidence module. Like the hub
  // events this skips page_slug/page_kind injection: the module renders on the
  // hub and on two ad landings, none of which is a ComparePage. PostHog's own
  // `$current_url` identifies the originating page, and `surface` distinguishes
  // the several instances a single /compare/* detail page can carry.
  | {
      event: "compare_help_link_clicked";
      /** The `/help/...` path the reader followed. */
      target_href: string;
      /** The <CompareHelpLinks> `surface` prop — product slug, or surface key. */
      surface: string;
    };

/**
 * Subset of `CompareEvent` that represents click events whose names
 * map to a short `CompareClickEventName` in data attributes.
 */
export type CompareClickEvent = Extract<
  CompareEvent,
  {
    event:
      | "compare_pricing_cta_clicked"
      | "compare_start_free_cta_clicked"
      | "compare_related_link_clicked"
      | "compare_source_link_clicked"
      | "compare_business_plan_clicked"
      | "compare_site_control_clicked"
      | "compare_handshq_comparison_clicked"
      | "compare_contact_sales_clicked"
      | "compare_help_link_clicked";
  }
>;

/* -------------------------------------------------------------------------- */
/*                          data-attribute helper                             */
/* -------------------------------------------------------------------------- */

/**
 * Type-safe authoring helper for compare block data attributes.
 *
 * Usage in a server component:
 *   <Link href="/pricing" {...compareEventAttrs("pricing_cta")}>
 *
 * The string-literal parameter makes typos a compile error instead of
 * a silently-dropped event at runtime.
 */
export function compareEventAttrs(
  name: CompareClickEventName
): { "data-compare-event": CompareClickEventName } {
  return { "data-compare-event": name };
}

/**
 * Maps the short click-attr name (`"pricing_cta"`) to the full event
 * name (`"compare_pricing_cta_clicked"`). This is the only place the
 * two shapes are reconciled — the click delegator in
 * <CompareTracker> calls this to translate a DOM attribute into a
 * CompareEvent.
 */
export function clickAttrToEventName(
  attr: CompareClickEventName
): CompareClickEvent["event"] {
  switch (attr) {
    case "pricing_cta":
      return "compare_pricing_cta_clicked";
    case "start_free_cta":
      return "compare_start_free_cta_clicked";
    case "related_link":
      return "compare_related_link_clicked";
    case "source_link":
      return "compare_source_link_clicked";
    case "business_plan":
      return "compare_business_plan_clicked";
    case "site_control":
      return "compare_site_control_clicked";
    case "handshq_comparison":
      return "compare_handshq_comparison_clicked";
    case "contact_sales":
      return "compare_contact_sales_clicked";
    case "help_link":
      return "compare_help_link_clicked";
  }
}

/* -------------------------------------------------------------------------- */
/*                           trackCompareEvent wrapper                        */
/* -------------------------------------------------------------------------- */

/**
 * Client-side wrapper around `posthog.capture()` for every Phase 49
 * compare event.
 *
 * For click and view events, pass the `page` so the wrapper can
 * inject the shared `page_slug` / `page_kind` / `reviewed_at` props.
 *
 * For `compare_assisted_signup` (fired off-compare from the onboarding
 * route), omit `page` — the event carries its own slug/kind in its
 * payload.
 */
export function trackCompareEvent(
  event: CompareEvent,
  page?: Pick<ComparePage, "slug" | "kind" | "reviewedAt">
): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;

  // compare_assisted_signup is the off-page variant — minimal payload.
  if (event.event === "compare_assisted_signup") {
    const { event: name, ...props } = event;
    posthog.capture(name, props);
    return;
  }

  // Phase 50 hub events — fired from /compare (not a ComparePage), so
  // no page_slug / page_kind / reviewed_at injection. The hub is the
  // origin surface; target_slug / target_kind / filter_* carry the
  // engagement signal on their own.
  if (
    event.event === "compare_hub_card_clicked" ||
    event.event === "compare_hub_filter_applied" ||
    // #1857 — the Help evidence module renders on the hub and on two ad
    // landings as well as on /compare/* detail pages, so it cannot depend on a
    // ComparePage being in scope. `$current_url` carries the page identity.
    event.event === "compare_help_link_clicked"
  ) {
    const { event: name, ...props } = event;
    posthog.capture(name, props);
    return;
  }

  // Everything else is on-page and needs the shared props.
  if (!page) {
    // Defensive: on-page events require a page. Silently drop rather
    // than firing a malformed event.
    return;
  }
  const { event: name, ...eventProps } = event;
  posthog.capture(name, {
    page_slug: page.slug,
    page_kind: page.kind,
    reviewed_at: page.reviewedAt,
    ...eventProps,
  });

  if (
    event.event === "compare_section_viewed" ||
    event.event === "compare_faq_section_viewed"
  ) {
    capturePublishingEvent("publishing_section_viewed", {
      surface: "comparison",
      comparison_page: page.slug,
      section_id: event.section_id,
    });
    return;
  }

  const ctaTypeByEvent: Partial<Record<CompareEvent["event"], string>> = {
    compare_pricing_cta_clicked: "pricing",
    compare_start_free_cta_clicked: "start_free",
    compare_related_link_clicked: "related_comparison",
    compare_source_link_clicked: "source_link",
    compare_business_plan_clicked: "business_plan",
    compare_site_control_clicked: "site_control",
    compare_handshq_comparison_clicked: "handshq_comparison",
    compare_contact_sales_clicked: "contact_sales",
  };
  const ctaType = ctaTypeByEvent[event.event];
  if (!ctaType) return;

  capturePublishingEvent("publishing_cta_clicked", {
    surface: "comparison",
    comparison_page: page.slug,
    cta_type: ctaType,
    cta_location: "position" in event ? event.position : undefined,
    selected_plan:
      event.event === "compare_business_plan_clicked"
        ? "business"
        : event.event === "compare_site_control_clicked"
          ? "site_control"
          : event.event === "compare_start_free_cta_clicked"
            ? "starter"
            : undefined,
    target_url:
      event.event === "compare_source_link_clicked"
        ? event.target_url
        : undefined,
    target_slug:
      event.event === "compare_related_link_clicked"
        ? event.target_slug
        : undefined,
  });
}

/* -------------------------------------------------------------------------- */
/*                  person-level property (secondary signal)                  */
/* -------------------------------------------------------------------------- */

/**
 * Initialize the `compare_pages_viewed_count` person property.
 *
 * Called from `<CompareTracker>` on mount. Useful for segmenting
 * "explored many compares" users but NOT load-bearing for attribution.
 *
 * posthog-js 1.360+ removed the deprecated `posthog.people.increment`
 * API. Client-side atomic increments are no longer a supported path.
 * Instead we use `setPersonProperties({}, { ... })` with the second
 * argument (`userPropertiesToSetOnce`) to stamp `compare_pages_viewed_count = 1`
 * on first view only. The actual count of compare-page engagements
 * per user is derived in PostHog from the `compare_section_viewed`
 * event stream (which is where the real signal lives anyway — see
 * 49-CONTEXT.md Grey Area 2 "secondary signal, not load-bearing").
 *
 * Name kept as `incrementComparePagesViewedCount` so callers in Plans
 * 02/03 don't need to be renamed — the intent (segment "explored many
 * compares" users) is unchanged, only the storage mechanism.
 */
export function incrementComparePagesViewedCount(): void {
  if (typeof window === "undefined") return;
  if (!posthog.__loaded) return;
  // Second arg is `userPropertiesToSetOnce` — only initialized if unset.
  posthog.setPersonProperties({}, { compare_pages_viewed_count: 1 });
}
